import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { BadgeModule } from 'primeng/badge';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { GuidelineCategory, UserGuidelinesFacadeService } from './user-guidelines-facade.service';
import { AccordionModule } from 'primeng/accordion';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { hugePdf02 } from '@ng-icons/huge-icons';
import { radixQuestionMarkCircled, radixExternalLink, radixFileText, radixClipboard } from '@ng-icons/radix-icons';

@Component({
  selector: 'cis-user-guidelines',
  imports: [DialogModule, PopoverModule, BadgeModule, TranslatePipe, AccordionModule, NgIcon],
  viewProviders: [
    provideIcons({
      hugePdf02,
      radixQuestionMarkCircled,
      radixExternalLink,
      radixFileText,
      radixClipboard,
    }),
  ],
  template: `
    <div class="flex justify-content-center align-items-center">
      <span class="p-overlay-badge" (click)="op.toggle($event)">
        <i class="pi pi-question-circle text-3xl cursor-pointer"></i>
      </span>
    </div>
    <p-popover #op>
      <div [style.width.px]="540">
        <h5 class="m-0 font-semibold border-bottom-1 pb-3 border-noround" [style.border-color]="'var(--p-surface-200)'">
          {{ 'user_manual' | translate }}
        </h5>
        @if (op.overlayVisible) {
          <p-accordion [(value)]="activePanel" [multiple]="false">
            @for (category of guidelineCategories(); track category) {
              @let guidelinesOfCategory = guidelines().get(category) ?? [];
              <p-accordion-panel [value]="category" [disabled]="guidelinesOfCategory.length === 0">
                <p-accordion-header>
                  <ng-template #toggleicon let-active="active">
                    @if (active) {
                      <i class="pi pi-chevron-up"></i>
                    } @else {
                      <i class="pi pi-chevron-down"></i>
                    }
                  </ng-template>
                  <span class="flex align-items-center gap-2">
                    <span class="font-medium whitespace-nowrap flex align-items-center gap-2">
                      <ng-icon [name]="facade.getIconByCategory(category)" size="20" />
                      {{ facade.getTitleByCategory(category) | translate }}
                    </span>
                    <p-badge
                      severity="secondary"
                      [value]="guidelinesOfCategory.length"
                      class="ml-auto mr-2"
                      [pt]="badgePt" />
                  </span>
                </p-accordion-header>
                <p-accordion-content>
                  @if (activePanel() === category) {
                    <div class="flex flex-column gap-2">
                      @for (guideline of guidelinesOfCategory; track guideline.title) {
                        <a [href]="guideline.pdf" target="_blank" class="no-underline text-color block">
                          <div class="guideline-item flex align-items-center p-2 border-round cursor-pointer">
                            <div class="flex align-items-center justify-content-center mr-3" style="min-width: 32px;">
                              <ng-icon name="hugePdf02" size="24" [style.color]="'var(--p-red-500)'" />
                            </div>
                            <span class="font-medium flex-1 mr-2">{{ guideline.title }}</span>
                            <ng-icon name="radixExternalLink" size="20" />
                          </div>
                        </a>
                      }
                    </div>
                  }
                </p-accordion-content>
              </p-accordion-panel>
            }
          </p-accordion>
        }
      </div>
    </p-popover>
  `,
  styles: `
    .guideline-item {
      border: 1px solid var(--p-surface-200);
      transition: background-color 0.15s;
    }
    .guideline-item:hover {
      background-color: var(--p-surface-100) !important;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserGuidelinesComponent {
  protected readonly facade = inject(UserGuidelinesFacadeService);
  protected readonly badgePt = {
    root: {
      style: {
        borderRadius: '0.25rem',
      },
    },
  };

  activePanel = signal<GuidelineCategory | undefined>(undefined);

  guidelines = this.facade.guidelines;
  guidelineCategories = this.facade.guidelineCategories;
}
